import React from 'react';
import { motion } from 'framer-motion';
import CountdownSection from './CountdownSection.tsx';
import RSVPSection from './RSVPSection.tsx';
import { 
  EVENT_DATE,
  EVENT_DATE_DISPLAY,
  EVENT_DAY,
  VENUE_NAME,
  VENUE_ADDRESS,
  DRESS_CODE,
  MORNING_SESSION,
  AFTERNOON_SESSION,
  EVENT_HASHTAG,
  GREETING,
  ORGANIZER,
  EVENT_NAME,
  RSVP_DEADLINE
} from '../constants/event.ts';
import { COLORS } from '../constants/colors.ts';
import { fadeIn, slideUp, staggerChildren } from '../constants/animations.ts';

interface MainContentProps {
  rsvpSectionRef: React.RefObject<HTMLElement | null>;
}

const MainContent: React.FC<MainContentProps> = ({ rsvpSectionRef }) => {
  return (
    <>
      {/* Greeting Section */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerChildren}
        className="py-12 text-center px-4 relative"
        style={{ backgroundColor: COLORS.primary }}
      >
        <motion.div variants={slideUp} className="space-y-6">
          <p className="font-optima text-base" style={{ color: COLORS.secondary }}>
            {GREETING}
          </p>
          <p className="font-awesome-lathusca text-2xl font-bold" style={{ color: COLORS.secondary }}>
            {ORGANIZER}
          </p>
          <p className="font-optima text-sm" style={{ color: COLORS.secondary }}>
            would like to invite you to
          </p>
          <h2 className="font-awesome-lathusca text-3xl font-bold" style={{ color: COLORS.secondary }}>
            {EVENT_NAME}
          </h2>
        </motion.div>
      </motion.section>

      {/* Details Section */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerChildren}
        className="py-12 relative overflow-hidden"
        style={{ backgroundColor: COLORS.primary }}
      >            
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-24 h-24 border-l-2 border-t-2 opacity-30" style={{ borderColor: COLORS.secondary }} />
        <div className="absolute bottom-0 right-0 w-24 h-24 border-r-2 border-b-2 opacity-30" style={{ borderColor: COLORS.secondary }} />
        
        <div className="relative z-10 px-4 space-y-12">
          <motion.div variants={slideUp} className="space-y-3">
            <h3 className="font-awesome-lathusca text-xl uppercase mb-2 text-center font-bold" style={{ color: COLORS.secondary }}>Date</h3>
            <div className="text-center space-y-1">
              <p className="font-optima text-base font-medium tracking-wide" style={{ color: COLORS.secondary }}>{EVENT_DATE_DISPLAY} ({EVENT_DAY})</p>
            </div>
          </motion.div>

          <motion.div variants={slideUp} className="space-y-3">
            <h3 className="font-awesome-lathusca text-xl uppercase mb-2 text-center font-bold" style={{ color: COLORS.secondary }}>Time</h3>
            <div className="text-center space-y-2">
              <p className="font-optima text-base tracking-wide" style={{ color: COLORS.secondary }}>
                <strong>Morning Session</strong><br />
                9.00 a.m. - 12.00 p.m.<br />
                <span className="text-sm">(Football, Badminton, Pickleball)</span>
              </p>
              <p className="font-optima text-base tracking-wide" style={{ color: COLORS.secondary }}>
                <strong>Afternoon Session</strong><br />
                3.00 p.m. - 6.00 p.m.<br />
                <span className="text-sm">(Hi-Tea)</span><br />
                <span className="italic text-sm">Payment is required for this session</span>
              </p>
            </div>
          </motion.div>
          
          <motion.div variants={slideUp} className="space-y-3">
            <h3 className="font-awesome-lathusca text-xl uppercase mb-2 text-center font-bold" style={{ color: COLORS.secondary }}>Venue</h3>
            <p className="font-optima text-base font-medium text-center tracking-wide" style={{ color: COLORS.secondary }}>{VENUE_NAME}</p>
            <p className="font-optima text-sm text-center" style={{ color: COLORS.secondary }} dangerouslySetInnerHTML={{ __html: VENUE_ADDRESS.replace(/\n/g, '<br />') }} />
          </motion.div>

          <motion.div variants={slideUp} className="space-y-3">
            <h3 className="font-awesome-lathusca text-xl uppercase mb-2 text-center font-bold" style={{ color: COLORS.secondary }}>Dress Code</h3>
            <p className="font-optima text-base font-medium text-center tracking-wide" style={{ color: COLORS.secondary }}>{DRESS_CODE}</p>
          </motion.div>

        </div>
      </motion.section>

      {/* Countdown Section */}
      <CountdownSection weddingDate={EVENT_DATE} />

      {/* Hashtag Section */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
        className="py-16 text-center"
        style={{ backgroundColor: COLORS.primary }}
      >
        <motion.div variants={slideUp} className="space-y-4">
          <h3 className="font-awesome-lathusca text-3xl font-bold mb-2 mb-8" style={{ color: COLORS.secondary }}>Share Your Moments</h3>
          <p className="font-alex-brush text-3xl font-medium" style={{ color: COLORS.secondary }}>
            {EVENT_HASHTAG}
          </p>
        </motion.div>
      </motion.section>

      {/* RSVP Section */}
      <RSVPSection rsvpSectionRef={rsvpSectionRef} />

      {/* Music Credit Section */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
        className="py-12 text-center"
        style={{ backgroundColor: COLORS.primary }}
      >
        <motion.div variants={slideUp} className="space-y-3">
          <h3 className="font-awesome-lathusca text-xl uppercase mb-2 font-bold" style={{ color: COLORS.secondary }}>Background Music</h3>
          <p className="font-optima text-base" style={{ color: COLORS.secondary }}>
            Guitar by Noah Zikry (ALAM Cadet)
          </p>
        </motion.div>
      </motion.section>

      {/* Footer */}
      <motion.footer 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
        className="py-6 mb-12 space-y-2"
        style={{ backgroundColor: COLORS.primary, color: COLORS.secondary }}
      >
        <p className="text-center text-sm opacity-75 pb-5">© 2025 Strada. All rights reserved.</p>
      </motion.footer>
    </>
  );
};

export default MainContent;
